import * as React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { RootLayout } from '@/components/layout';
import { LoginPage, ProfilePage } from '@/components/auth';
import { authService } from '@/services/auth';
import { versionService } from '@/services/version';
import { UpdateNotification } from '@/components/ui/UpdateNotification';
import ViewEmailStandalone from './ViewEmailStandalone';
import EmailChatPage from '@/components/email/EmailChatPage';
import EmailSearchPage from '@/components/email/EmailSearchPage';
import ComposeModal from '@/components/email/ComposeModal';
import { ComposeProvider, useCompose } from '@/contexts/ComposeContext';
import { PaginationProvider } from '@/contexts/PaginationContext';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { Toaster } from 'react-hot-toast';
import InboxList from '@/components/InboxList';

// Configure React Router future flags to suppress warnings
const router = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  },
};

// Simple test component
const TestComponent = () => {
  // console.log('TestComponent is rendering!');
  return (
    <div style={{ padding: '20px', background: 'red', color: 'white' }}>
      <h1>Test Component Working!</h1>
      <p>If you can see this, React is rendering properly.</p>
    </div>
  );
};

// Test email component
const TestEmailComponent = () => {
  // console.log('TestEmailComponent is rendering!');
  return (
    <div style={{ 
      padding: '20px', 
      background: 'green', 
      color: 'white',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <h1>Email View Test Component Working!</h1>
      <p>If you can see this, the email route is working properly.</p>
      <p>This should replace the entire page layout.</p>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Public Route Component - redirects to home if already authenticated
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  const location = useLocation();
  const isAddingAccount = location.state?.addAccount;
  
  if (isAuthenticated && !isAddingAccount) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// App Content Component
const AppContent: React.FC = () => {
  const { isComposeOpen, closeCompose, openCompose } = useCompose();
  const [showUpdateNotification, setShowUpdateNotification] = React.useState(false);

  React.useEffect(() => {
    // Start version check when component mounts
    versionService.startVersionCheck(() => {
      setShowUpdateNotification(true);
    });

    // Cleanup on unmount
    return () => {
      versionService.stopVersionCheck();
    };
  }, []);
  
  return (
    <>
      <Router future={router.future}>
        <Routes>
          {/* Test routes */}
          <Route path="/test" element={<TestComponent />} />
          <Route path="/test-email" element={
            <ProtectedRoute>
              <TestEmailComponent />
            </ProtectedRoute>
          } />
          
          {/* Login route - public route that redirects if already authenticated */}
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          
          {/* Protected routes with layout */}
          <Route element={
            <ProtectedRoute>
              <RootLayout />
            </ProtectedRoute>
          }>
            <Route index element={<InboxList onCompose={openCompose} />} />
            <Route path="inbox" element={<InboxList folderName="INBOX" onCompose={openCompose} />} />
            <Route path="sent" element={<InboxList folderName="Sent" onCompose={openCompose} />} />
            <Route path="starred" element={<InboxList folderName="Starred" onCompose={openCompose} />} />
            <Route path="drafts" element={<InboxList folderName="Drafts" onCompose={openCompose} />} />
            <Route path="archive" element={<InboxList folderName="Archive" onCompose={openCompose} />} />
            <Route path="spam" element={<InboxList folderName="Spam" onCompose={openCompose} />} />
            <Route path="trash" element={<InboxList folderName="Trash" onCompose={openCompose} />} />
            <Route path="junk" element={<InboxList folderName="Junk" onCompose={openCompose} />} />
            <Route path="settings" element={<div>Settings</div>} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="viewemail" element={<ViewEmailStandalone />} />
            <Route path="email-chat" element={<EmailChatPage />} />
            <Route path="email-search" element={<EmailSearchPage />} />
            <Route path="email/:folderPath/:id/:uid" element={<EmailChatPage />} />
            {/* <Route path="email/:folderPath/:id" element={<EmailChatPage />} /> */}
            {/* Dynamic route for custom folders */}
            <Route path="folder/:folderName" element={<InboxList onCompose={openCompose} />} />
          </Route>
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
      
      {/* Compose Modal */}
      <ComposeModal isOpen={isComposeOpen} onClose={closeCompose} />

      {/* Update Notification */}
      {showUpdateNotification && (
        <UpdateNotification
          onDismiss={() => setShowUpdateNotification(false)}
        />
      )}
    </>
  );
};

function App() {
  // console.log('App component is rendering!');
  
  return (
    <ComposeProvider>
      <PaginationProvider>
        <SidebarProvider>
          <Toaster
            position="top-center"
            containerStyle={{
              zIndex: 10050,
            }}
            toastOptions={{
              style: {
                background: '#ffffff',
                color: '#1f2937',
                padding: '16px',
                borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                fontSize: '14px',
                fontWeight: '500',
              },
            }}
          />
          <AppContent />
        </SidebarProvider>
      </PaginationProvider>
    </ComposeProvider>
  );
}

export default App; 